import type { Dict } from "./types";

export const en: Dict = {
  app: {
    name: "Aksaraku",
    tagline: "Learn while playing",
  },
  common: {
    on: "On",
    off: "Off",
    back: "Back",
    soon: "Coming soon",
    loading: "Just a moment...",
  },
  nav: {
    parent: "Parents",
    home: "Home",
  },
  profiles: {
    title: "Who is playing?",
    subtitle: "Pick a child or add a new profile",
    add: "Add Child",
    namePlaceholder: "Child name",
    language: "Language",
    start: "Start",
    empty: "No profiles yet. Add a child first.",
    errors: {
      emptyName: "Name is required",
      nameTooLong: "Name is too long",
      invalidLocale: "Unknown language",
    },
  },
  home: {
    greeting: "Hi, {{name}}!",
    tracing: "Writing",
    games: "Games",
    rewards: "Collection",
    avatar: "Character",
  },
  play: {
    title: "Let's Write",
    pick: "Pick a letter, number, or shape",
    retry: "Try Again",
    next: "Next",
    great: "Great job!",
    strokeProgress: "Line {{current}} of {{total}}",
    offTrack: "Follow the line",
    listen: "Listen",
    canvasLabel: "Writing area for {{glyph}}",
    objectHint: "{{glyph}} for {{object}}",
    color: "Color",
  },
  games: {
    title: "Games",
    matching: "Matching",
    balloon: "Pop the Balloon",
    coloring: "Coloring",
    modeCase: "Big - Small",
    modeObject: "Letter - Picture",
    casePrompt: "Find the lowercase letter",
    objectPrompt: "Which letter is this?",
    balloonPrompt: "Pop the balloon: {{letter}}",
    repeat: "Repeat",
    correct: "Correct!",
    wrong: "Try again",
    playAgain: "Play Again",
    score: "{{correct}} of {{total}} correct",
    colorHint: "Pick a color, then tap the letter",
    stickers: "Stickers",
    clear: "Clear",
    finish: "Finish",
    finished: "Great work!",
    paint: "Color",
    painted: "{{painted}} of {{total}} parts colored",
  },
  preview: {
    title: "Content Preview",
    hint: "Check the shape of every letter, number, and shape.",
  },
  rewards: {
    title: "Reward Collection",
    kind: {
      sticker: "Stickers",
      trophy: "Trophies",
      character: "Characters",
    },
    unlocked: "You earned a new reward!",
    yeay: "Yay!",
    locked: "Locked",
    done: "Done",
    requirement: {
      itemsAttempted: "Try {{count}} activities",
      itemsMastered: "Master {{count}} letters or numbers",
      starsEarned: "Earn {{count}} stars",
      totalAttempts: "Play {{count}} times",
      kindMastered: "Master {{count}} in one category",
    },
  },
  avatar: {
    title: "My Character",
    slot: {
      color: "Color",
      ears: "Ears",
      eyes: "Eyes",
      accessory: "Accessory",
    },
  },
  categories: {
    upper: "Uppercase",
    lower: "Lowercase",
    digits: "Numbers",
    shapes: "Shapes",
  },
  mastery: {
    new: "New",
    learning: "Learning",
    familiar: "Getting there",
    mastered: "Mastered",
  },
  parent: {
    title: "Parent Area",
    gateTitle: "Parents Only",
    gate: {
      question: "What is {{a}} + {{b}}?",
      placeholder: "Answer",
      submit: "Enter",
      wrong: "That answer is not right",
      cancel: "Cancel",
    },
    progressFor: "{{name}}'s progress",
    progressQuick: "{{mastered}} mastered • {{attempted}} tried • ⭐ {{stars}}",
    report: "Progress Report",
    summary: "Summary",
    statMastered: "Mastered",
    statAttempted: "Tried",
    statStars: "Stars",
    statAttempts: "Total Attempts",
    levels: "Mastery Levels",
    byCategory: "By Category",
    needsPractice: "Needs Practice",
    noWeak: "Everything tried so far is mastered. Well done!",
    practiceDetail: "Accuracy {{score}}% • {{attempts}} tries",
    tracingQuality: "Writing Quality",
    noTracing: "No writing practice yet.",
    avgAccuracy: "Average accuracy",
    traces: "Practice count",
    neverPlayed: "No activity yet.",
    lastPlayed: "Last played: {{date}}",
    screenTime: "Time Limit",
    sessionLimit: "Session limit",
    breakReminder: "Break reminder",
    breakOff: "Off",
    minutes: "{{minutes}} minutes",
    sessionRemaining: "Time left in this session: {{time}}",
    settings: "Settings",
    language: "Language",
    haptics: "Vibration",
    audio: "Sound",
    reduceMotion: "Reduce motion",
    switchProfile: "Switch Child",
    preview: "Content Preview",
    manageChildren: "Manage Children",
    activeChild: "Active",
    switchTo: "Select",
    delete: "Delete",
    deleteConfirm: "Delete for sure?",
    deleteHint: "Deleting a child also removes their progress and collection.",
    lastChildHint: "There must always be at least one child.",
    childLanguage: "Language for {{name}}",
    cancel: "Cancel",
  },
  session: {
    loading: "One moment…",
    expiredTitle: "Time for a break",
    expiredHint:
      "Today's learning session is over. Ask a parent to unlock it again.",
    parentUnlock: "Parent",
    breakReminder: "You have been learning for {{minutes}} minutes. Let's rest!",
    breakDone: "Rested",
  },
  settings: {
    errors: {
      invalidLocale: "Invalid language",
      invalidSessionLimit: "Invalid session limit",
      invalidBreakReminder: "Invalid break reminder",
    },
  },
  error: {
    storage: "Could not save data. Please try again.",
    retry: "Try Again",
    reset: "Reset Data",
    resetHint: "Deletes all profiles and progress on this device.",
    crash: "Oops, something went wrong. Let's try that again.",
    crashRetry: "Try Again",
  },
  landing: {
    nav: {
      install: "Install App",
      skip: "Skip to content",
    },
    hero: {
      tagline: "Learn while playing",
      subtitle: "Interactive education app for toddlers 3+. Write, play, and learn — no internet, no ads.",
      ctaInstall: "Install Now",
      ctaHow: "See how it works",
    },
    features: {
      title: "What's in Aksaraku?",
      writing: { title: "Writing", desc: "Trace letters, numbers, and shapes with step-by-step guidance." },
      games: { title: "Games", desc: "Match, pop balloons, and color while learning." },
      rewards: { title: "Rewards", desc: "Collect stickers, trophies, and characters with every practice." },
      parent: { title: "Parent Dashboard", desc: "Track progress, set time limits, and manage child profiles." },
    },
    how: {
      title: "How to Start",
      step1: { title: "Install", desc: "Add to home screen" },
      step2: { title: "Choose Child", desc: "Create a profile for your little one" },
      step3: { title: "Start Learning", desc: "Pick a letter, number, or game" },
    },
    trust: {
      title: "Why Aksaraku?",
      offline: "Works without internet",
      noAds: "No ads, no interruptions",
      private: "Data stays on your device",
      multiChild: "Profiles for each child",
    },
    languages: {
      title: "Three Languages",
      id: "Bahasa Indonesia",
      en: "English",
      ar: "العربية (RTL)",
    },
    footer: {
      cta: "Get Started",
      madeBy: "Made with ❤️ by Rohmad's AI Engineering Team",
    },
    install: {
      instructions: "How to Install",
      ios: "Tap the Share button, then choose \"Add to Home Screen\".",
      android: "Tap the menu, then choose \"Add to Home screen\".",
      desktop: "Click the install icon in the address bar, or use the browser menu.",
      openApp: "Open App",
    },
  },
};
